import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { ROUTES } from "../app/routes.js";
import { HqApiError, authApi as api } from "../shared/api/auth.js";
import { useSession } from "../app/session-store.js";
import { useT } from "../i18n/index.js";
import { LocaleSwitch } from "../components/LocaleSwitch.js";

/**
 * Đăng nhập nhân viên nội bộ.
 *
 * KHÔNG có liên kết « đăng ký » hay « quên mật khẩu ». Tài khoản do chủ cấp; mất mật khẩu thì hỏi
 * chủ (`docs/RUNBOOK.md` §2). Thêm một đường tự phục hồi vào trung tâm kiểm soát toàn hệ sinh thái
 * là mở một bề mặt tấn công để tiết kiệm một tin nhắn.
 */
export function LoginPage() {
  const t = useT("auth");
  const navigate = useNavigate();
  const setSession = useSession((s) => s.setSession);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [dangGui, setDangGui] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setDangGui(true);
    try {
      const me = await api.login({ email, password });
      setSession(me);
      // Còn mật khẩu tạm thì máy chủ chặn mọi đường khác — đưa thẳng tới chỗ đổi, đừng để người dùng
      // đâm vào một màn 403 rồi tự đoán.
      navigate(me.mustChangePassword ? ROUTES.changePassword : ROUTES.attention, { replace: true });
    } catch (err) {
      setError(err instanceof HqApiError ? err.message : t("serverUnreachable"));
    } finally {
      setDangGui(false);
    }
  }

  /** Chỉ có ở bản `vite` dev — Vite xoá cả nhánh này khỏi bản build production. Máy chủ cũng tự 404 ngoài local. */
  async function devLogin() {
    setError(null);
    setDangGui(true);
    try {
      await api.devLogin();
      const me = await api.me();
      setSession(me);
      navigate(me.mustChangePassword ? ROUTES.changePassword : ROUTES.attention, { replace: true });
    } catch (err) {
      setError(err instanceof HqApiError ? err.message : t("devServerUnreachable"));
    } finally {
      setDangGui(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center gap-6 p-6">
      <header>
        <div className="mb-4 flex justify-end">
          <LocaleSwitch />
        </div>
        <h1 className="text-2xl font-semibold">Bube HQ</h1>
        <p className="text-sm opacity-70">{t("login.subtitle")}</p>
      </header>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1 text-sm">
          {t("email")}
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded border bg-background px-3 py-2"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          {t("password")}
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="rounded border bg-background px-3 py-2"
          />
        </label>

        {error ? (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        ) : null}

        <button type="submit" disabled={dangGui} className="rounded bg-foreground px-3 py-2 text-background disabled:opacity-50">
          {dangGui ? t("login.submitting") : t("login.submit")}
        </button>
      </form>

      {import.meta.env.DEV ? (
        <button
          type="button"
          onClick={() => void devLogin()}
          disabled={dangGui}
          className="rounded border border-dashed px-3 py-2 text-sm disabled:opacity-50"
        >
          {t("login.dev")}
        </button>
      ) : null}
    </main>
  );
}
